import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { Select } from '@app/components/core/input/select/Select'
import { Toggle, type ToggleOption } from '@app/components/core/toggle/Toggle'
import { YesNoToggle } from '@app/components/core/toggle/YesNoToggle'
import { TagSelectionGroup } from '../blocks/TagSelectionGroup'
import { CreationStepLayout } from '../blocks/CreationStepLayout'
import { useQuery } from '@based/sdk/react'
import {
  NUMBER_OF_HOMES,
  OWNERSHIP_MODE,
  RENT_SEGMENT,
  type NumberOfHomes,
  type OwnershipMode,
  type RentSegment,
  type OnboardingStep,
} from '@schema/initiative'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInitiativeAutoSave } from '../../shared/useInitiativeAutoSave'
import { useTranslations } from '@i18n/useLanguage'
import { Style } from 'inlines'
import { SelectableTagModel } from '@app/types/TagModel'
import type { InitiativeWithId } from '../../shared/initiativeTypes'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../../shared/workflow'
import { PhaseType, PHASE_TYPES } from '@schema/phase'

const DEFAULT_WORKFLOW_STEP = initiativeCreationSteps.initiativeDetails

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}

const yesNoToggleProps = {
  optionStyle: { flexGrow: 1 },
  containerStyle: { width: '100%' },
} as const

const inferOwnershipMode = (initiative: InitiativeWithId): OwnershipMode =>
  initiative.ownershipMode ??
  (initiative.rentalBudget ? OWNERSHIP_MODE.rent : OWNERSHIP_MODE.buy)

const normalizeOwnershipModeForPhase = (
  mode: OwnershipMode,
  initiative: InitiativeWithId,
  isLivingPhase: boolean,
): OwnershipMode => {
  if (!isLivingPhase) {
    return mode
  }

  if (mode === OWNERSHIP_MODE.buy || mode === OWNERSHIP_MODE.rent) {
    return mode
  }

  return initiative.rentalBudget && !initiative.buyBudget
    ? OWNERSHIP_MODE.rent
    : OWNERSHIP_MODE.buy
}

export const InitiativeDetailsStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  workflowStep = DEFAULT_WORKFLOW_STEP,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
  workflowStep?: OnboardingStep
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const commonTranslations = useTranslations().app.common
  const emptyLabel = commonTranslations.notSpecified

  const developmentFormsQuery = useQuery('list-development-forms')
  const housingFormsQuery = useQuery('list-housing-forms')
  const purchaseBudgetQuery = useQuery('list-purchase-budget')
  const rentBudgetQuery = useQuery('list-rent-budget')

  const [ownershipMode, setOwnershipMode] = useState<OwnershipMode>(() =>
    normalizeOwnershipModeForPhase(
      inferOwnershipMode(initiative),
      initiative,
      selectedPhaseType === PHASE_TYPES.living,
    ),
  )
  const [numberOfHomes, setNumberOfHomes] = useState<NumberOfHomes | null>(
    initiative.numberOfHomes ?? null,
  )
  const [developmentFormId, setDevelopmentFormId] = useState<number | null>(
    initiative.developmentForm?.id ?? null,
  )
  const [selectedHousingForms, setSelectedHousingForms] = useState<number[]>(
    initiative.housingForms?.map((housingForm) => housingForm.id) ?? [],
  )
  const [buyBudgetId, setBuyBudgetId] = useState<number | null>(
    initiative.buyBudget?.id ?? null,
  )
  const [rentBudgetId, setRentBudgetId] = useState<number | null>(
    initiative.rentalBudget?.id ?? null,
  )
  const [rentSegment, setRentSegment] = useState<RentSegment | null>(() => {
    if (initiative.rentSegment) {
      return initiative.rentSegment
    }
    const mode = inferOwnershipMode(initiative)
    if (mode === OWNERSHIP_MODE.rent || mode === OWNERSHIP_MODE.mix) {
      return RENT_SEGMENT.middle
    }
    return null
  })
  const [kvkRegistration, setKvkRegistration] = useState(
    initiative.kvkRegistration ?? false,
  )

  const isIdeaPhase = selectedPhaseType === PHASE_TYPES.idea
  const isLivingPhase = selectedPhaseType === PHASE_TYPES.living
  const showKvkRegistration = !isIdeaPhase && !isLivingPhase
  const showsRentFields =
    ownershipMode === OWNERSHIP_MODE.rent ||
    (!isLivingPhase && ownershipMode === OWNERSHIP_MODE.mix)
  const showsBuyPrice =
    ownershipMode === OWNERSHIP_MODE.buy ||
    (!isLivingPhase && ownershipMode === OWNERSHIP_MODE.mix)

  const handleOwnershipModeChange = useCallback((mode: OwnershipMode) => {
    setOwnershipMode(mode)
    if (mode === OWNERSHIP_MODE.buy) {
      setRentBudgetId(null)
      setRentSegment(null)
      return
    }
    if (mode === OWNERSHIP_MODE.rent) {
      setBuyBudgetId(null)
      setRentSegment((prev) => prev ?? RENT_SEGMENT.middle)
      return
    }
    if (mode === OWNERSHIP_MODE.mix) {
      setRentSegment((prev) => prev ?? RENT_SEGMENT.middle)
      return
    }
    if (mode === OWNERSHIP_MODE.dontKnow) {
      setBuyBudgetId(null)
      setRentBudgetId(null)
      setRentSegment(null)
    }
  }, [])

  const savePayload = useMemo(
    () => ({
      developmentForm: developmentFormId ? { id: developmentFormId } : null,
      numberOfHomes: numberOfHomes ?? undefined,
      housingForms: selectedHousingForms,
      ownershipMode,
      ...(isLivingPhase
        ? { kvkRegistration: true }
        : showKvkRegistration
          ? { kvkRegistration }
          : {}),
      buyBudget: showsBuyPrice && buyBudgetId ? { id: buyBudgetId } : null,
      rentalBudget:
        showsRentFields && rentBudgetId ? { id: rentBudgetId } : null,
      rentSegment: showsRentFields
        ? (rentSegment ?? RENT_SEGMENT.middle)
        : null,
      onboardingStep: workflowStep,
    }),
    [
      developmentFormId,
      numberOfHomes,
      selectedHousingForms,
      ownershipMode,
      showKvkRegistration,
      isLivingPhase,
      kvkRegistration,
      showsBuyPrice,
      buyBudgetId,
      showsRentFields,
      rentBudgetId,
      rentSegment,
      workflowStep,
    ],
  )

  useInitiativeAutoSave(initiative.id, savePayload)

  const allHousingForms: SelectableTagModel<number>[] = useMemo(
    () =>
      (housingFormsQuery.data ?? []).map(
        (item: { id: number; title: string }) => ({
          key: item.id,
          value: item.title,
          selected: selectedHousingForms.includes(item.id),
        }),
      ),
    [housingFormsQuery.data, selectedHousingForms],
  )

  const selectHousingForm = useCallback(
    (id: number) => {
      setSelectedHousingForms((prev) => {
        const index = prev.indexOf(id)
        return index > -1 ? prev.toSpliced(index, 1) : [...prev, id]
      })
    },
    [setSelectedHousingForms],
  )

  const buyRentOptions = useMemo((): ToggleOption<OwnershipMode>[] => {
    const modes = isLivingPhase
      ? [OWNERSHIP_MODE.buy, OWNERSHIP_MODE.rent]
      : Object.values(OWNERSHIP_MODE)

    return modes.map((value) => ({
      label: translations.ownershipModeOptions[value] ?? emptyLabel,
      value,
      style: { flexGrow: 1 },
    }))
  }, [isLivingPhase, translations.ownershipModeOptions, emptyLabel])

  const rentSegmentOptions = useMemo(
    (): ToggleOption<RentSegment>[] =>
      Object.values(RENT_SEGMENT).map((value) => ({
        label: translations.rentSegmentOptions[value] ?? emptyLabel,
        value,
        style: { flexGrow: 1 },
      })),
    [translations.rentSegmentOptions, emptyLabel],
  )

  const numberOfHomesOptions = useMemo(
    () =>
      Object.values(NUMBER_OF_HOMES).map((item) => ({
        value: item,
        label: translations.numberOfHomesOptions[item] ?? emptyLabel,
      })),
    [translations.numberOfHomesOptions, emptyLabel],
  )

  const developmentFormOptions = useMemo(
    () =>
      (developmentFormsQuery.data ?? []).map(
        (item: { id: number; name: string }) => ({
          value: item.id,
          label: item.name,
        }),
      ),
    [developmentFormsQuery.data],
  )

  const purchaseBudgetOptions = useMemo(
    () =>
      (purchaseBudgetQuery.data ?? []).map(
        (item: { id: number; title: string }) => ({
          value: item.id,
          label: item.title,
        }),
      ),
    [purchaseBudgetQuery.data],
  )

  const rentBudgetOptions = useMemo(
    () =>
      (rentBudgetQuery.data ?? []).map(
        (item: { id: number; name: string }) => ({
          value: item.id,
          label: item.name,
        }),
      ),
    [rentBudgetQuery.data],
  )

  useEffect(() => {
    completeStep(workflowStep)
  }, [completeStep, workflowStep])

  const isSwappedStep = workflowStep !== DEFAULT_WORKFLOW_STEP

  return (
    <CreationStepLayout
      selectedPhaseType={selectedPhaseType}
      currentStep={workflowStep}
      titleOverride={
        isSwappedStep
          ? translations.creationStepTitles[DEFAULT_WORKFLOW_STEP]
          : undefined
      }
    >
      <Column gap="2rem" alignItems="stretch">
        {showKvkRegistration && (
          <Column gap="0.5rem" alignItems="stretch">
            <Text.Medium style={labelStyle}>
              {translations.kvkRegistration}
            </Text.Medium>
            <YesNoToggle
              yesSelected={kvkRegistration}
              setYesSelected={setKvkRegistration}
              {...yesNoToggleProps}
            />
          </Column>
        )}

        <Column gap="0.5rem" alignItems="stretch">
          <Text.Medium style={labelStyle}>
            {translations.initiativeDetailsBuyRentQuestion}
          </Text.Medium>
          <Toggle<OwnershipMode>
            options={buyRentOptions}
            selectedOption={ownershipMode}
            setSelectedOption={handleOwnershipModeChange}
            style={{ width: '100%' }}
          />
        </Column>

        {showsRentFields ? (
          <>
            <Column gap="0.5rem" alignItems="stretch">
              <Text.Medium style={labelStyle}>
                {translations.initiativeDetailsRentSegmentQuestion}
              </Text.Medium>
              <Toggle<RentSegment>
                options={rentSegmentOptions}
                selectedOption={rentSegment ?? RENT_SEGMENT.middle}
                setSelectedOption={setRentSegment}
                style={{ width: '100%' }}
              />
            </Column>
            <Select<number>
              label={translations.initiativeDetailsPriceLabel}
              labelStyle={labelStyle}
              value={rentBudgetId}
              setValue={setRentBudgetId}
              notSelectedLabel={emptyLabel}
              options={rentBudgetOptions}
            />
          </>
        ) : null}

        <Select<number>
          label={translations.developmentForm}
          labelStyle={labelStyle}
          value={developmentFormId}
          setValue={setDevelopmentFormId}
          notSelectedLabel={emptyLabel}
          options={developmentFormOptions}
        />

        <TagSelectionGroup
          label={translations.initiativeDetailsThemes}
          subTitle={translations.selectOneOrMore}
          collapsedAmountVisible={Math.max(selectedHousingForms.length, 5)}
          tags={allHousingForms}
          onSelect={selectHousingForm}
        />

        {showsBuyPrice ? (
          <>
            <Select<NumberOfHomes>
              label={translations.initiativeDetailsNumberOfHouseholds}
              labelStyle={labelStyle}
              value={numberOfHomes}
              setValue={setNumberOfHomes}
              notSelectedLabel={emptyLabel}
              options={numberOfHomesOptions}
            />
            <Select<number>
              label={translations.initiativeDetailsPricePerM2Label}
              labelStyle={labelStyle}
              value={buyBudgetId}
              setValue={setBuyBudgetId}
              notSelectedLabel={emptyLabel}
              options={purchaseBudgetOptions}
            />
          </>
        ) : null}
      </Column>
    </CreationStepLayout>
  )
}
